<template>
  <el-main class="blocked-numbers-tab">
    <div v-if="!cloudApiEnabled" class="blocked-numbers-disabled">
      <el-alert
        type="warning"
        :closable="false"
        title="Blocked numbers are available only for WhatsApp Cloud API numbers."
      />
    </div>

    <template v-else>
      <div class="blocked-numbers-toolbar">
        <div class="blocked-numbers-search">
          <el-input
            v-model="search"
            clearable
            size="small"
            class="blocked-numbers-search-input"
            placeholder="Search number"
            @keyup.enter.native="loadBlockedNumbers"
            @clear="loadBlockedNumbers"
          />
          <el-select
            v-model="statusFilter"
            size="small"
            class="blocked-numbers-status-filter"
            placeholder="Status"
            @change="handleStatusFilterChange"
          >
            <el-option label="All" value="all" />
            <el-option label="Blocked" value="blocked" />
            <el-option label="Unblocked" value="unblocked" />
          </el-select>
          <el-button size="small" @click="loadBlockedNumbers">Search</el-button>
        </div>

        <div v-if="showAdvanced" class="blocked-numbers-actions">
          <span class="blocked-numbers-sync">
            Last sync:
            <strong>{{ formattedLastSyncedAt }}</strong>
          </span>
          <el-tag v-if="cacheStale" type="warning" size="small">Stale</el-tag>
          <el-button
            size="small"
            :loading="syncing"
            icon="el-icon-refresh"
            @click="syncBlockedNumbers"
          >
            Refresh from Meta
          </el-button>
        </div>
      </div>

      <el-alert
        v-if="cacheError"
        class="blocked-numbers-alert"
        type="warning"
        :closable="true"
        :title="cacheError"
        @close="cacheError = ''"
      />

      <el-alert
        v-if="mutationError"
        class="blocked-numbers-alert"
        type="error"
        :closable="true"
        :title="mutationError"
        @close="mutationError = ''"
      />

      <el-alert
        v-if="mutationResult"
        class="blocked-numbers-alert"
        :type="mutationResult.failed_numbers.length > 0 ? 'warning' : 'success'"
        :closable="true"
        :title="mutationResultTitle"
        @close="mutationResult = null"
      >
        <div v-if="mutationResult.failed_numbers.length > 0">
          Failed: {{ mutationResult.failed_numbers.join(", ") }}
        </div>
      </el-alert>

      <el-table
        v-loading="loading"
        :data="blockedNumbers"
        border
        size="small"
        class="blocked-numbers-table"
      >
        <el-table-column label="User" min-width="220">
          <template slot-scope="scope">
            {{ getBlockedUserIdentifier(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column label="Status" min-width="120">
          <template slot-scope="scope">
            <el-tag size="small" :type="getStatusTagType(scope.row.status)">
              {{ scope.row.status || "-" }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Blocked At" min-width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.blocked_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Blocked By" min-width="180">
          <template slot-scope="scope">
            {{ scope.row.blocked_by || scope.row.created_by || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="Remark" min-width="220">
          <template slot-scope="scope">
            {{ scope.row.remark || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="Unblocked At" min-width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.unblocked_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Unblocked By" min-width="180">
          <template slot-scope="scope">
            {{ scope.row.unblocked_by || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="Last Synced" min-width="180">
          <template slot-scope="scope">
            {{ formatDate(scope.row.last_synced_at) }}
          </template>
        </el-table-column>
        <el-table-column label="Action" width="130" fixed="right">
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.status === 'blocked'"
              size="mini"
              type="danger"
              plain
              :loading="unblockingNumber === getBlockedUserIdentifier(scope.row)"
              @click="unblockNumber(scope.row)"
            >
              Unblock
            </el-button>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="blocked-numbers-pagination">
        <el-pagination
          background
          layout="prev, pager, next, sizes, total"
          :current-page.sync="page"
          :page-size.sync="pageSize"
          :page-sizes="[25, 50, 100, 200]"
          :total="total"
          @current-change="loadBlockedNumbers"
          @size-change="handlePageSizeChange"
        />
      </div>
    </template>
  </el-main>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { mapGetters } from "vuex";

export default {
  name: "BlockedNumbersTab",
  props: {
    wa_endpoint: String,
    cloudApiEnabled: Boolean,
  },
  data() {
    return {
      blockedNumbers: [],
      search: "",
      page: 1,
      pageSize: 50,
      statusFilter: "all",
      total: 0,
      lastSyncedAt: null,
      cacheStale: true,
      loading: false,
      syncing: false,
      unblockingNumber: "",
      cacheError: "",
      mutationError: "",
      mutationResult: null,
    };
  },
  computed: {
    ...mapGetters(["showAdvanced"]),
    formattedLastSyncedAt() {
      return this.formatDate(this.lastSyncedAt);
    },
    mutationResultTitle() {
      if (!this.mutationResult) {
        return "";
      }

      const acceptedCount = this.mutationResult.accepted_numbers.length;
      const failedCount = this.mutationResult.failed_numbers.length;
      return `${acceptedCount} accepted${failedCount > 0 ? `, ${failedCount} failed` : ""}`;
    },
  },
  watch: {
    wa_endpoint() {
      this.resetAndLoad();
    },
    cloudApiEnabled() {
      this.resetAndLoad();
    },
  },
  mounted() {
    this.resetAndLoad();
  },
  methods: {
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return moment(value).format("DD MMM YYYY HH:mm");
    },
    getErrorMessage(err, fallback) {
      return (
        _.get(err, "response.data.error.message") ||
        _.get(err, "response.data.message") ||
        _.get(err, "message") ||
        fallback
      );
    },
    getListPayload() {
      return {
        wa_endpoint: this.wa_endpoint,
        page: this.page,
        page_size: this.pageSize,
        search: this.search,
        status: this.statusFilter,
      };
    },
    getStatusTagType(status) {
      if (status === "blocked") {
        return "danger";
      }
      if (status === "unblocked") {
        return "success";
      }
      return "info";
    },
    getBlockedUserIdentifier(row) {
      return row?.wa_id || row?.user_id || "";
    },
    applyListResponse(response) {
      this.blockedNumbers = response?.data || [];
      this.total = response?.pagination?.total || 0;
      this.lastSyncedAt = response?.cache?.last_synced_at || null;
      this.cacheStale = !!response?.cache?.stale;
    },
    resetAndLoad() {
      this.blockedNumbers = [];
      this.search = "";
      this.page = 1;
      this.statusFilter = "all";
      this.total = 0;
      this.lastSyncedAt = null;
      this.cacheStale = true;
      this.cacheError = "";
      this.mutationError = "";
      this.mutationResult = null;

      if (this.cloudApiEnabled && this.wa_endpoint) {
        this.loadBlockedNumbers();
      }
    },
    loadBlockedNumbers() {
      if (!this.cloudApiEnabled || !this.wa_endpoint) {
        return;
      }

      this.loading = true;
      this.cacheError = "";
      this.$store
        .dispatch("WHATSAPP_GET_BLOCKED_NUMBERS", {
          data: this.getListPayload(),
        })
        .then((response) => {
          this.applyListResponse(response);
        })
        .catch((err) => {
          this.cacheError = this.getErrorMessage(err, "Failed to load blocked numbers.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    syncBlockedNumbers() {
      if (!this.cloudApiEnabled || !this.wa_endpoint) {
        return;
      }

      this.syncing = true;
      this.cacheError = "";
      this.$store
        .dispatch("WHATSAPP_SYNC_BLOCKED_NUMBERS", {
          data: {
            ...this.getListPayload(),
            force: true,
          },
        })
        .then((response) => {
          this.applyListResponse(response);
          this.$message({
            type: "success",
            message: "Blocked numbers refreshed.",
          });
        })
        .catch((err) => {
          this.cacheError = this.getErrorMessage(
            err,
            "Failed to refresh blocked numbers from Meta."
          );
        })
        .finally(() => {
          this.syncing = false;
        });
    },
    unblockNumber(row) {
      const number = this.getBlockedUserIdentifier(row);
      if (!number || row?.status !== "blocked") {
        return;
      }

      this.unblockingNumber = number;
      this.mutationError = "";
      this.mutationResult = null;
      this.$store
        .dispatch("WHATSAPP_UNBLOCK_NUMBERS", {
          data: {
            wa_endpoint: this.wa_endpoint,
            numbers: [number],
          },
        })
        .then((response) => {
          this.mutationResult = {
            accepted_numbers: response?.accepted_numbers || [],
            failed_numbers: response?.failed_numbers || [],
          };
          this.loadBlockedNumbers();
        })
        .catch((err) => {
          this.mutationError = this.getErrorMessage(err, "Failed to unblock number.");
        })
        .finally(() => {
          this.unblockingNumber = "";
        });
    },
    handlePageSizeChange() {
      this.page = 1;
      this.loadBlockedNumbers();
    },
    handleStatusFilterChange() {
      this.page = 1;
      this.loadBlockedNumbers();
    },
  },
};
</script>

<style scoped>
.blocked-numbers-tab {
  padding: 20px 0;
}

.blocked-numbers-disabled {
  max-width: 720px;
}

.blocked-numbers-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.blocked-numbers-search,
.blocked-numbers-actions,
.blocked-numbers-form-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.blocked-numbers-search {
  width: auto;
  flex-wrap: wrap;
}

.blocked-numbers-search-input {
  width: 260px;
}

.blocked-numbers-status-filter {
  flex: 0 0 130px;
  width: 130px;
}

.blocked-numbers-status-filter >>> .el-input {
  width: 130px;
}

.blocked-numbers-sync {
  color: #606266;
  font-size: 13px;
}

.blocked-numbers-form {
  max-width: 720px;
  margin-bottom: 16px;
}

.blocked-numbers-form-actions {
  justify-content: space-between;
  margin-top: 8px;
  color: #606266;
  font-size: 13px;
}

.blocked-numbers-alert {
  margin-bottom: 12px;
}

.blocked-numbers-table {
  width: 100%;
}

.blocked-numbers-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
